"use client"

import { CircleHelp, Wrench } from "lucide-react"
import * as React from "react"

import { useAuth } from "../hooks/useAuth.js"
import { useOfficePermissions } from "../hooks/useOfficePermissions.js"
import { isReleaseNotesEmailAllowlisted } from "../lib/releaseNotesAllowlist.js"

import { filterNavByPermissions, OFFICE_NAV } from "./app-sidebar-nav.js"
import { NavMain } from "./nav-main.js"
import { NavSecondary } from "./nav-secondary.js"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "./ui/sidebar.js"

// Check if in development mode
const isDevelopment = import.meta.env.DEV;

// Release-notes menu item — visible ONLY when the caller's email matches
// the client-side allowlist mirror (client/lib/releaseNotesAllowlist.ts).
// This is a UX gate, not a security gate — the backend still enforces via
// the release-notes route allowlist + a global preHandler on the generic
// generated CRUD routes. If a non-allowlisted user typed /devtools/release-notes
// directly, the routeGate in routes.tsx would redirect + the API would 403.
const RELEASE_NOTES_ITEM = {
  title: "Release Notes",
  url: "/devtools/release-notes",
};

const HELP_NAV = [{ title: "Help", url: "/help", icon: CircleHelp }];

// Dev menu - only shows in development
const DEV_NAV = isDevelopment ? [{
  title: "🔧 Dev Tools",
  url: "/dev",
  icon: Wrench,
  isActive: true,
  items: [
    {
      title: "Mobile Debug",
      url: "/debug"
    }
  ]
}] : [];

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useAuth();
  const showReleaseNotes = isReleaseNotesEmailAllowlisted(user?.email ?? null);
  // Office RBAC: show only the pages the user's roles grant (union of every
  // role; admins see everything). Permissions come from /auth/me, so they are
  // final once auth has loaded — no fail-open while loading.
  const { canAny } = useOfficePermissions();

  // Compose navMain per-render so the Release Notes item can be gated
  // by the current user's email. Slot it in as its own top-level entry
  // (with the Dev Tools wrench icon) rather than nesting it under the
  // dev-only "🔧 Dev Tools" group — the URL is `/devtools/release-notes`
  // but the feature is meant for allowlisted admins in prod too, not
  // just local dev.
  const permittedNav = filterNavByPermissions(OFFICE_NAV, canAny);
  const navMain = showReleaseNotes
    ? [
        ...DEV_NAV,
        ...permittedNav,
        {
          title: "Release Notes",
          url: RELEASE_NOTES_ITEM.url,
          icon: Wrench,
          items: [],
        },
      ]
    : [...DEV_NAV, ...permittedNav];

  return (
    <Sidebar collapsible="icon" {...props} variant="sidebar">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              {/* Assume the parent Sidebar component adds 'group' or handles state propagation */}
              <a href="#" className="flex items-center gap-2 group"> {/* Added flex, gap, and group */}
                {/* Collapsed Logo Container: Visible when sidebar group state is collapsed */}
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-none text-sidebar-primary-foreground group-data-[state=expanded]:hidden">
                  <img src="/lithos-logo_logo-dark.png" alt="Lithos Logo Icon" className="h-full w-full object-contain dark:invert" />
                </div>
                {/* Expanded Logo Container: Visible when sidebar group state is expanded */}
                <div className="hidden flex-1 group-data-[state=collapsed]:hidden group-data-[state=expanded]:block">
                  <img src="/lithos-logo_logo-long-dark.png" alt="Lithos Logo" className="h-8 w-auto dark:invert" />
                </div>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={navMain} />
        {/* <NavProjects projects={data.projects} /> */}
      </SidebarContent>
      <SidebarFooter>
        {/* Help: no perms, every logged-in role sees it (HELP-D2). */}
        <NavSecondary items={HELP_NAV} className="p-0" />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
